import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CampaignDialog from "./CampaignDialog";

const formatCharacterClass = (character, t) => {
  const classEntry = character.characterClass?.[0];
  if (!classEntry?.id) return t("characters.noClass");
  return t(`system.classes.${classEntry.id}`);
};

const ParticipantPickerDialog = ({
  availableCharacters,
  isLoading,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (isOpen) setSelectedIds([]);
  }, [isOpen]);

  const sortedCharacters = useMemo(
    () =>
      [...availableCharacters].sort((first, second) =>
        (first.characterName || "").localeCompare(second.characterName || "")
      ),
    [availableCharacters]
  );

  const toggleCharacter = (characterId) => {
    setSelectedIds((current) =>
      current.includes(characterId)
        ? current.filter((value) => value !== characterId)
        : [...current, characterId]
    );
  };

  const handleSubmit = () => {
    if (selectedIds.length === 0) return;
    onSubmit(selectedIds);
  };

  return (
    <CampaignDialog
      description={t("campaigns.addParticipantHint")}
      isOpen={isOpen}
      onClose={onClose}
      title={t("campaigns.addParticipant")}
      titleEyebrow={t("campaigns.sections.participantsEyebrow")}
      wide
    >
      {isLoading ? (
        <p>{t("campaigns.loadingCharacters")}</p>
      ) : sortedCharacters.length === 0 ? (
        <p>{t("campaigns.noCharactersToAdd")}</p>
      ) : (
        <div className="campaign-list-grid campaign-dialog-list">
          {sortedCharacters.map((character) => (
            <label className="campaign-member-card campaign-select-card" key={character.id}>
              <input
                checked={selectedIds.includes(character.id)}
                type="checkbox"
                onChange={() => toggleCharacter(character.id)}
              />
              <h3>{character.characterName || t("characters.unnamed")}</h3>
              <p>
                {character.race?.id
                  ? t(`system.races.${character.race.id}`)
                  : t("characters.noRace")}
              </p>
              <p className="campaign-member-meta">
                {formatCharacterClass(character, t)}
              </p>
            </label>
          ))}
        </div>
      )}
      <footer className="campaign-dialog-footer">
        <button className="campaign-ghost-button" type="button" onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          className="campaign-primary-button"
          disabled={selectedIds.length === 0}
          type="button"
          onClick={handleSubmit}
        >
          {t("campaigns.confirmParticipants")}
        </button>
      </footer>
    </CampaignDialog>
  );
};

export default ParticipantPickerDialog;
